import { useGetWarrantyProvidersQuery } from '@/services/warranties';
import { Box, Button, FormikSelectInputNative, FormikTextInput, Modal } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikValues } from 'formik';
import { SyntheticEvent, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType, warrantyMap, WarrantyPaymentFrequency, WarrantyType } from 'types';

export const DuctlessMiniSplitWarrantyModal = ({
  isModalOpen,
  closeModal,
  warrantyType,
  onSubmit,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  warrantyType?: string;
  onSubmit: (values: FormikValues) => void;
}) => {
  const { programType = ProgramType.hvac } = useParams<{ programType: string }>();
  const warranty = warrantyMap[programType as ProgramType][warrantyType as WarrantyType];

  const { data: warrantyProviderResponse } = useGetWarrantyProvidersQuery({});

  const warrantyProviderOptions = useMemo(() => {
    const programTypeProviders = warrantyProviderResponse?.data?.find(
      (item) => item.programType === programType,
    )?.providers;

    const warrantyProviderList = programTypeProviders?.map((p) => p.name) || [];
    return warrantyProviderList.map((element) => {
      return {
        label: element,
        value: element,
      };
    });
  }, [programType, warrantyProviderResponse]);

  return (
    <Modal ariaLabeledBy="ductlessMiniSplitWarranty" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="ductlessMiniSplitWarranty" title={`Add ${warranty}`} onDismiss={closeModal} />
      <Formik
        initialValues={{
          warrantyProvider: '',
          paymentFrequency: WarrantyPaymentFrequency.upfront,
          indoorOutdoorCost: 0,
          additionalIndoorCost: 0,
        }}
        onSubmit={({ warrantyProvider, paymentFrequency, ...rest }) =>
          onSubmit({
            warrantyProvider,
            paymentFrequency,
            warrantyType: WarrantyType.ductlessMiniSplit,
            ductlessMiniSplit: { ...rest },
          })
        }
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <Modal.Body>
                <Box direction="column" gap="md">
                  <Field
                    type="select"
                    component={FormikSelectInputNative}
                    label="Warranty Provider"
                    name="warrantyProvider"
                    id="warrantyProvider"
                    options={warrantyProviderOptions}
                    isRequired
                  />
                  <Field
                    type="select"
                    component={FormikSelectInputNative}
                    label="Payment Frequency"
                    name="paymentFrequency"
                    id="paymentFrequency"
                    options={Object.keys(WarrantyPaymentFrequency).map((key) => ({
                      label: WarrantyPaymentFrequency[key as keyof typeof WarrantyPaymentFrequency],
                      value: key,
                    }))}
                    isRequired
                  />
                  <Field
                    type="number"
                    component={FormikTextInput}
                    label="Warranty Cost for 1 indoor & 1 outdoor unit"
                    name="indoorOutdoorCost"
                    id="indoorOutdoorCost"
                    isRequired
                  />
                  <Field
                    type="number"
                    component={FormikTextInput}
                    label="Warranty Cost for additional indoor units"
                    name="additionalIndoorCost"
                    id="additionalIndoorCost"
                    isRequired
                  />
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  as="button"
                  onClick={() => {
                    closeModal();
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button size="md" type="submit" isLoading={isSubmitting}>
                  Add Option
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
